import Vue from 'vue'
import VueRouter from 'vue-router'
import { clearAdminSession, getAdminUserInfo, hasAdminSession } from '@/utils/adminAuth'
import { getMemberToken } from '@/utils/memberAuth'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('./views/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('./views/Admin.vue'),
    redirect: () => {
      const userInfo = getAdminUserInfo()
      return userInfo?.userType === 'supplier'
        ? '/admin/supplier-portal/order'
        : '/admin/dashboard'
    },
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('./views/Dashboard.vue')
      },
      {
        path: 'coupon',
        name: 'CouponList',
        component: () => import('./views/CouponList.vue')
      },
      {
        path: 'coupon-list',
        redirect: '/admin/coupon'
      },
      {
        path: 'coupon-create',
        name: 'CouponCreate',
        component: () => import('./views/CouponCreate.vue')
      },
      {
        path: 'coupon/whitelist',
        name: 'WhitelistDistribute',
        component: () => import('./views/WhitelistDistribute.vue'),
        meta: { title: '白名单发放' }
      },
      {
        path: 'coupon/cardkey',
        name: 'CardKeyDistribute',
        component: () => import('./views/CardKeyDistribute.vue'),
        meta: { title: '卡密发放' }
      },
      {
        path: 'coupon/record',
        name: 'DistributeRecord',
        component: () => import('./views/DistributeRecord.vue'),
        meta: { title: '发放记录' }
      },
      {
        path: 'coupon/redemption-codes',
        name: 'RedemptionCodeManagement',
        component: () => import('./views/RedemptionCodeManagement.vue'),
        meta: { title: '管理兑换码' }
      },
      {
        path: 'coupon/complete',
        name: 'DistributeComplete',
        component: () => import('./views/DistributeComplete.vue'),
        meta: { title: '分发完成' }
      },
      {
        path: 'order',
        name: 'Order',
        component: () => import('./views/OrderList.vue')
      },
      {
        path: 'product',
        name: 'Product',
        component: () => import('./views/ProductManagement.vue')
      },
      {
        path: 'freight-template',
        name: 'FreightTemplate',
        component: () => import('./views/FreightTemplateManagement.vue'),
        meta: { title: '运费模板' }
      },
      {
        path: 'supplier',
        name: 'Supplier',
        component: () => import('./views/SupplierManagement.vue')
      },
      {
        path: 'member',
        name: 'Member',
        component: () => import('./views/MemberManagement.vue')
      },
      {
        path: 'sms',
        name: 'Sms',
        component: () => import('./views/SmsManagement.vue')
      },
      {
        path: 'batch-toolbox',
        name: 'BatchToolbox',
        component: () => import('./views/BatchToolbox.vue'),
        meta: { title: '批量工具箱' }
      },
      {
        path: 'aftersale',
        name: 'AfterSale',
        component: () => import('./views/AfterSaleList.vue')
      },
      // CRM管理(销售端)
      {
        path: 'crm/customer',
        name: 'CrmCustomer',
        component: () => import('./views/CrmCustomer.vue'),
        meta: { title: '客户管理' }
      },
      {
        path: 'crm/customer/detail',
        name: 'CrmCustomerDetail',
        component: () => import('./views/CrmCustomerDetail.vue'),
        meta: { title: '客户详情' }
      },
      {
        path: 'crm/coupon-application',
        name: 'CrmCouponApplication',
        component: () => import('./views/CouponApplication.vue'),
        meta: { title: '业务员卡券申请' }
      },
      {
        path: 'crm/order',
        name: 'CrmOrder',
        component: () => import('./views/CrmOrder.vue'),
        meta: { title: '销售订单' }
      },
      {
        path: 'crm/analysis',
        name: 'CrmAnalysis',
        component: () => import('./views/CrmAnalysis.vue'),
        meta: { title: '客户分析' }
      },
      // 采购管理
      {
        path: 'purchase/supplier',
        name: 'PurchaseSupplier',
        component: () => import('./views/SupplierManagement.vue'),
        meta: { title: '供应商管理' }
      },
      {
        path: 'purchase/order',
        name: 'PurchaseOrder',
        component: () => import('./views/PurchaseOrder.vue'),
        meta: { title: '采购单' }
      },
      {
        path: 'purchase/payment',
        name: 'PurchasePayment',
        component: () => import('./views/PaymentList.vue'),
        meta: { title: '付款单' }
      },
      // 财务管理
      {
        path: 'finance/payment',
        name: 'FinancePayment',
        component: () => import('./views/FinancePayment.vue'),
        meta: { title: '付款审核' }
      },
      {
        path: 'finance/bill',
        name: 'FinanceBill',
        component: () => import('./views/FinanceBill.vue'),
        meta: { title: '账单管理' }
      },
      // 供货商端
      {
        path: 'supplier-portal/order',
        name: 'SupplierOrder',
        component: () => import('./views/SupplierOrder.vue'),
        meta: { title: '我的订单' }
      },
      {
        path: 'supplier-portal/upload',
        name: 'SupplierUpload',
        component: () => import('./views/SupplierUpload.vue'),
        meta: { title: '上传单号' }
      },
      {
        path: 'supplier-portal/product',
        name: 'SupplierProduct',
        component: () => import('./views/SupplierProduct.vue'),
        meta: { title: '产品管理' }
      },
      {
        path: 'supplier-portal/stock',
        name: 'SupplierStock',
        component: () => import('./views/SupplierStock.vue'),
        meta: { title: '库存管理' }
      },
      // 系统管理
      {
        path: 'system/department',
        name: 'Department',
        component: () => import('./views/DepartmentManagement.vue'),
        meta: { title: '部门管理' }
      },
      {
        path: 'system/role',
        name: 'Role',
        component: () => import('./views/RoleManagement.vue'),
        meta: { title: '角色管理' }
      },
      {
        path: 'system/permission',
        name: 'Permission',
        component: () => import('./views/PermissionManagement.vue'),
        meta: { title: '权限管理' }
      },
      {
        path: 'system/user',
        name: 'UserManagement',
        component: () => import('./views/UserManagement.vue'),
        meta: { title: '用户管理' }
      },
      {
        path: 'system/site-content',
        name: 'SiteContentSettings',
        component: () => import('./views/SiteContentSettings.vue'),
        meta: { title: '前台内容设置' }
      },
    ]
  },
  {
    path: '/',
    redirect: '/admin'
  },
  {
    path: '/client',
    name: 'ClientHome',
    component: () => import('./views/LoginView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/phone-home',
    name: 'PhoneHome',
    component: () => import('./views/PhoneHome.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/bind-card',
    name: 'ClientBindCard',
    component: () => import('./views/ClientBindCard.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/gift-exchange',
    name: 'GiftExchange',
    component: () => import('./views/GiftExchange.vue'),
    meta: { requiresAuth: false }  // 礼包展示页面公开访问，兑换时再验证卡密
  },
  {
    path: '/gift-verify',
    name: 'GiftCardVerify',
    component: () => import('./views/GiftCardVerify.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/gift-select',
    name: 'GiftSelect',
    component: () => import('./views/GiftSelect.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/gift-success',
    name: 'GiftSuccess',
    component: () => import('./views/GiftSuccess.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/gift-exchange-detail',
    name: 'GiftExchangeDetail',
    component: () => import('./views/GiftExchangeDetail.vue'),
    meta: { requiresClientAuth: true }
  },
  {
    path: '/my-orders',
    name: 'MyOrders',
    component: () => import('./views/ClientOrderList.vue'),
    meta: { requiresMemberAuth: true }
  },
  {
    path: '/my-coupons',
    name: 'MyCoupons',
    component: () => import('./views/MyCoupons.vue'),
    meta: { requiresMemberAuth: true }
  },
  {
    path: '/my-aftersales',
    name: 'MyAfterSales',
    component: () => import('./views/ClientAfterSaleList.vue'),
    meta: { requiresMemberAuth: true }
  },
  {
    path: '/my-orders/:id',
    name: 'MyOrderDetail',
    component: () => import('./views/ClientOrderDetail.vue'),
    meta: { requiresMemberAuth: true }
  },
  {
    path: '/delivery-plan/:couponInstanceId?',
    name: 'ClientDeliveryPlan',
    component: () => import('./views/ClientDeliveryPlan.vue'),
    meta: { requiresMemberAuth: true }
  },
]

const router = new VueRouter({
  mode: 'history',
  routes
})

// 修复 Vue Router 3.x 中 NavigationDuplicated 错误
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated' && 
        err.name !== 'NavigationCancelled' &&
        !err.message?.includes('Redirected')) {
      throw err
    }
  })
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => {
    if (err.name !== 'NavigationDuplicated' && 
        err.name !== 'NavigationCancelled' &&
        !err.message?.includes('Redirected')) {
      throw err
    }
  })
}

// 路由守卫
router.beforeEach((to, from, next) => {
  const cardInfo = localStorage.getItem('cardInfo')
  const memberToken = getMemberToken()
  const adminLoggedIn = hasAdminSession()
  
  // 后台管理需要认证的路由
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!adminLoggedIn) {
      clearAdminSession()
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    } else {
      const userInfo = getAdminUserInfo()
      if (userInfo?.userType === 'supplier') {
        const allowedPaths = new Set([
          '/admin',
          '/admin/supplier-portal/order',
          '/admin/supplier-portal/upload',
          '/admin/supplier-portal/product',
          '/admin/supplier-portal/stock'
        ])
        if (!allowedPaths.has(to.path)) {
          next('/admin/supplier-portal/order')
          return
        }
      }
      next()
    }
  } 
  // 会员登录可访问的路由（卡密登录或手机号登录）
  else if (to.matched.some(record => record.meta.requiresMemberAuth)) {
    if (!memberToken) {
      next({
        path: '/client',
        query: {
          view: 'login',
          redirect: to.fullPath
        }
      })
    } else {
      next()
    }
  }
  // 客户端需要卡密验证的路由
  else if (to.matched.some(record => record.meta.requiresClientAuth)) {
    if (!cardInfo) {
      const query = {
        redirect: to.fullPath
      }
      if (to.query?.couponId) {
        query.couponId = to.query.couponId
      }
      next({
        path: '/client',
        query
      })
    } else {
      next()
    }
  } 
  else {
    // 已登录用户访问登录页，跳转到首页
    if (to.path === '/login' && adminLoggedIn) {
      next('/admin')
    } else {
      next()
    }
  }
})

export default router
